import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Spin, Space, Typography, Collapse, Select, Button, List, Badge, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../ui';
import { STORAGE_KEYS } from '../../constants';
import { useGetFinanceStatisticsQuery } from '../../store/api/financeApi';
import { useGetGovernanceOverviewQuery } from '../../store/api/managementApi';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import { workspaceApi, type WorkspaceResponse, type WorkspaceTask } from '../../store/api/workspaceApi';
import { useGetWorkspaceQuery } from '../../store/api/workspaceApi';
import { useGetObligationsReadQuery, useGetPaymentsListQuery } from '../../store/api/financeApi';
import {
  buildFinanceProblemItems,
  buildLogistFinanceBlockers,
  DashboardFinanceKpiStrip,
  DashboardFinanceProblemsCard,
  DashboardLogistFinanceBlockers,
  DashboardPayablesBlock,
  DashboardReceivablesBlock,
} from '../../components/dashboard/DashboardFinanceSection';
import { useGetProfileQuery } from '../../store/api/authApi';
import { updateUser } from '../../store/slices/authSlice';
import ApplicationTaskModalHost from '../../components/applications/ApplicationTaskModalHost';
import GovernanceRiskHeader from '../../components/governance/GovernanceRiskHeader';
import { DashboardCompactQueues } from '../../components/dashboard/DashboardCompactQueues';
import { TASK_REGISTRY } from '../../constants/taskRegistry';
import { applicationCardPath, dashboardRoleKind, routeCardPath } from '../../utils/roleAwareEntityNavigation';
import {
  financeAttentionApplicationsCount,
  financeAttentionRoutesCount,
  overdueFinanceTasks,
  overdueOperationalTasks,
  tasksInVisibleEntities,
  workspaceTaskIsOverdue,
} from '../../domain/dashboardTaskKpiSelectors';
import { isFinanceWorkspaceTask } from '../../domain/workspaceTaskSemantics';
import {
  countDerivedFinancePressureSignals,
  deriveReactiveWorkflowProblems,
  logPhase5A1ReactiveTrace,
} from '../../domain/workflowDerivedProblems';
import { DashboardFinanceContextDrawer, type DashboardFinanceDrawerTarget } from '../../components/dashboard/DashboardFinanceContextDrawer';

const { Text } = Typography;

type DashboardQueueFilter = 'all' | 'active' | 'completed' | 'requires_closure' | 'attention';

function normalizeRole(role: string | null | undefined): string {
  return String(role || '').trim().toLowerCase();
}

function isFinanceRole(role: string): boolean {
  return role === 'accountant' || role === 'finance';
}

function isManagementRole(role: string): boolean {
  return role === 'admin' || role === 'manager' || role === 'director';
}

function isLogistLike(role: string): boolean {
  return role === 'logist' || role === 'dispatcher';
}

function isRequiresClosure(projection: any): boolean {
  return Boolean(projection?.physicalComplete && !projection?.overallComplete);
}

function isAttentionNeeded(projection: any, overdue: boolean, taskCount: number): boolean {
  if (overdue) return true;
  if (isRequiresClosure(projection)) return true;
  if (!projection) return taskCount > 0;
  if (!projection.operationalComplete || !projection.financialComplete || !projection.workflowComplete) return true;
  return taskCount > 0;
}

function taskLabel(task: WorkspaceTask): string {
  const meta = TASK_REGISTRY[String(task.taskType || '').toUpperCase()];
  return meta?.label ?? 'Задача';
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const authToken = useSelector((state: RootState) => state.auth.token);
  const role = normalizeRole(authUser?.role?.name);
  const dashboardRole = useMemo(() => dashboardRoleKind(authUser?.role?.name), [authUser?.role?.name]);
  const accessToken =
    authToken ||
    (() => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        return token && token !== 'undefined' && token !== 'null' ? token : null;
      } catch {
        return null;
      }
    })();
  const [queueFilter, setQueueFilter] = useState<DashboardQueueFilter>('attention');
  const [activeTask, setActiveTask] = useState<WorkspaceTask | null>(null);
  const [financeDrawerTarget, setFinanceDrawerTarget] = useState<DashboardFinanceDrawerTarget>(null);

  const shouldSyncProfile = Boolean(accessToken && (!authUser?.id || !authUser?.role?.name));
  const { data: profileData, isLoading: isProfileLoading } = useGetProfileQuery(undefined, {
    skip: !shouldSyncProfile,
  });
  useEffect(() => {
    if (profileData && typeof profileData === 'object' && 'id' in profileData) {
      dispatch(updateUser(profileData as any));
    }
  }, [profileData, dispatch]);

  const authReady = Boolean(authUser?.id && authUser?.role?.name);
  const shouldLoadDashboard = Boolean(accessToken && authReady);
  const needGovernanceForLayout = isManagementRole(role) || isFinanceRole(role);
  const { data: stats, isLoading: isLoadingStats } = useGetFinanceStatisticsQuery(
    {},
    { skip: !shouldLoadDashboard || !isManagementRole(role) }
  );
  const { data: governance, isLoading: isLoadingGovernance } = useGetGovernanceOverviewQuery(undefined, {
    skip: !shouldLoadDashboard || !needGovernanceForLayout,
  });
  const { data: workspaceRaw, isLoading: isLoadingWorkspace, refetch } = useGetWorkspaceQuery(undefined, {
    skip: !shouldLoadDashboard,
  });
  const shouldLoadFinanceQueues = isFinanceRole(role) || isManagementRole(role);
  const { data: incomingObligationsResp, isLoading: isLoadingIncomingObl } = useGetObligationsReadQuery(
    { page: 1, limit: 120, type: 'INCOMING' },
    { skip: !shouldLoadDashboard || !shouldLoadFinanceQueues }
  );
  const { data: outgoingObligationsResp, isLoading: isLoadingOutgoingObl } = useGetObligationsReadQuery(
    { page: 1, limit: 120, type: 'OUTGOING' },
    { skip: !shouldLoadDashboard || !shouldLoadFinanceQueues }
  );
  const { data: paymentsWideResp, isLoading: isLoadingPaymentsWide } = useGetPaymentsListQuery(
    { page: 1, limit: 200 },
    { skip: !shouldLoadDashboard || !shouldLoadFinanceQueues }
  );

  const workspace = (((workspaceRaw as { data?: WorkspaceResponse } | undefined)?.data ?? workspaceRaw) || undefined) as
    | WorkspaceResponse
    | undefined;
  const allTasks = useMemo(() => {
    const overdue = workspace?.overdueTasks ?? [];
    const mine = workspace?.myTasks ?? [];
    const m = new Map<number, WorkspaceTask>();
    [...overdue, ...mine].forEach((task: WorkspaceTask) => m.set(task.id, task));
    return Array.from(m.values());
  }, [workspace]);
  const routeTaskCounts = useMemo(() => {
    const out: Record<number, number> = {};
    allTasks.forEach((task: WorkspaceTask) => {
      if (task.routeId == null) return;
      out[task.routeId] = (out[task.routeId] ?? 0) + 1;
    });
    return out;
  }, [allTasks]);
  const applicationTaskCounts = useMemo(() => {
    const out: Record<number, number> = {};
    allTasks.forEach((task: WorkspaceTask) => {
      if (task.applicationId == null) return;
      out[task.applicationId] = (out[task.applicationId] ?? 0) + 1;
    });
    return out;
  }, [allTasks]);

  const visibleRoutes = useMemo(() => {
    const routes = workspace?.myRoutes ?? [];
    return routes.filter((route) => {
      const projection = (route as any).completionProjection;
      const taskCount = routeTaskCounts[route.id] ?? 0;
      const overdue = allTasks.some((task: WorkspaceTask) => task.routeId === route.id && workspaceTaskIsOverdue(task));
      if (queueFilter === 'all') return true;
      if (queueFilter === 'active') return !projection?.overallComplete;
      if (queueFilter === 'completed') return Boolean(projection?.overallComplete);
      if (queueFilter === 'requires_closure') return isRequiresClosure(projection);
      if (queueFilter === 'attention') return isAttentionNeeded(projection, overdue, taskCount);
      return true;
    });
  }, [workspace, routeTaskCounts, allTasks, queueFilter]);

  const visibleApplications = useMemo(() => {
    const applications = workspace?.myApplications ?? [];
    return applications.filter((application) => {
      const projection = (application as any).completionProjection;
      const taskCount = applicationTaskCounts[application.id] ?? 0;
      const overdue = allTasks.some((task: WorkspaceTask) => task.applicationId === application.id && workspaceTaskIsOverdue(task));
      if (queueFilter === 'all') return true;
      if (queueFilter === 'active') return !projection?.overallComplete;
      if (queueFilter === 'completed') return Boolean(projection?.overallComplete);
      if (queueFilter === 'requires_closure') return isRequiresClosure(projection);
      if (queueFilter === 'attention') return isAttentionNeeded(projection, overdue, taskCount);
      return true;
    });
  }, [workspace, applicationTaskCounts, allTasks, queueFilter]);

  const queueKpis = useMemo(() => {
    const routes = workspace?.myRoutes ?? [];
    const applications = workspace?.myApplications ?? [];
    const routeAttention = routes.filter((item: any) => {
      const taskCount = routeTaskCounts[item.id] ?? 0;
      const overdue = allTasks.some((task: WorkspaceTask) => task.routeId === item.id && workspaceTaskIsOverdue(task));
      return isAttentionNeeded(item.completionProjection, overdue, taskCount);
    }).length;
    const applicationAttention = applications.filter((item: any) => {
      const taskCount = applicationTaskCounts[item.id] ?? 0;
      const overdue = allTasks.some((task: WorkspaceTask) => task.applicationId === item.id && workspaceTaskIsOverdue(task));
      return isAttentionNeeded(item.completionProjection, overdue, taskCount);
    }).length;
    const accountantOnly = isFinanceRole(role) && !isManagementRole(role);
    if (accountantOnly) {
      const attention =
        financeAttentionRoutesCount(visibleRoutes, allTasks, isAttentionNeeded) +
        financeAttentionApplicationsCount(visibleApplications, allTasks, isAttentionNeeded);
      return { attention };
    }
    const attention = routeAttention + applicationAttention;
    return { attention };
  }, [workspace, routeTaskCounts, applicationTaskCounts, allTasks, role, visibleRoutes, visibleApplications]);

  const scopedWorkspaceTasks = useMemo(
    () => tasksInVisibleEntities(allTasks, visibleRoutes, visibleApplications),
    [allTasks, visibleRoutes, visibleApplications]
  );

  const financeOverdueKpi = useMemo(() => overdueFinanceTasks(scopedWorkspaceTasks).length, [scopedWorkspaceTasks]);
  const operationalOverdueKpi = useMemo(() => overdueOperationalTasks(scopedWorkspaceTasks).length, [scopedWorkspaceTasks]);
  const legacyOverdueKpi = useMemo(
    () =>
      scopedWorkspaceTasks.filter((t) => workspaceTaskIsOverdue(t) || String(t.status || '').toUpperCase() === 'OVERDUE').length,
    [scopedWorkspaceTasks]
  );

  const primaryOverdueKpi = useMemo(() => {
    if (isFinanceRole(role) && !isManagementRole(role)) return financeOverdueKpi;
    if (isLogistLike(role)) return operationalOverdueKpi;
    if (isManagementRole(role)) return financeOverdueKpi + operationalOverdueKpi;
    return legacyOverdueKpi;
  }, [role, financeOverdueKpi, operationalOverdueKpi, legacyOverdueKpi]);

  const primaryOverdueTitle = useMemo(() => {
    if (isFinanceRole(role) && !isManagementRole(role)) return 'Просроченные (фин.)';
    if (isLogistLike(role)) return 'Просроченные (опер.)';
    if (isManagementRole(role)) return 'Просроченные (всего)';
    return 'Просроченные задачи';
  }, [role]);

  const routesInWorkCount = useMemo(() => {
    return (workspace?.myRoutes ?? []).filter((r: any) => !r.completionProjection?.overallComplete).length;
  }, [workspace]);

  const applicationsInWorkCount = useMemo(() => {
    return (workspace?.myApplications ?? []).filter((a: any) => !a.completionProjection?.overallComplete).length;
  }, [workspace]);

  const incomingObligations = useMemo(
    () => incomingObligationsResp?.obligations ?? [],
    [incomingObligationsResp?.obligations]
  );
  const outgoingObligations = useMemo(
    () => outgoingObligationsResp?.obligations ?? [],
    [outgoingObligationsResp?.obligations]
  );
  const paymentsWide = paymentsWideResp?.payments ?? [];

  const pendingExpenseConfirmations = useMemo(
    () => allTasks.filter((t) => String(t.taskType || '').toUpperCase() === 'EXPENSE_CONFIRMATION').length,
    [allTasks]
  );

  const financeContextDrawerEnabled = isFinanceRole(role) || isManagementRole(role);

  const openFinanceDrawerCtx = useCallback((p: { kind: 'route' | 'application'; id: number }) => {
    setFinanceDrawerTarget(p);
  }, []);

  const financeProblemItems = useMemo(
    () =>
      buildFinanceProblemItems({
        incoming: incomingObligations,
        outgoing: outgoingObligations,
        routes: workspace?.myRoutes ?? [],
        governance,
        navigate,
        roleKind: dashboardRole,
        onOpenFinanceContext: financeContextDrawerEnabled ? openFinanceDrawerCtx : undefined,
      }),
    [
      incomingObligations,
      outgoingObligations,
      workspace,
      governance,
      navigate,
      dashboardRole,
      financeContextDrawerEnabled,
      openFinanceDrawerCtx,
    ]
  );

  const logistFinanceBlockers = useMemo(
    () => buildLogistFinanceBlockers(workspace?.myRoutes ?? [], allTasks),
    [workspace, allTasks]
  );

  const reactiveWorkflowProblems = useMemo(
    () =>
      deriveReactiveWorkflowProblems({
        allTasks,
        routes: workspace?.myRoutes ?? [],
        applications: workspace?.myApplications ?? [],
        roleName: authUser?.role?.name,
      }),
    [allTasks, workspace?.myRoutes, workspace?.myApplications, authUser?.role?.name]
  );

  const financeProblemsCount = useMemo(() => {
    const recon = governance?.financeGovernance?.reconciliationAnomalies ?? 0;
    const derivedFinance = countDerivedFinancePressureSignals({
      derived: reactiveWorkflowProblems,
      reconciliationAnomalies: recon,
    });
    return Math.max(financeProblemItems.length, derivedFinance);
  }, [financeProblemItems.length, reactiveWorkflowProblems, governance?.financeGovernance?.reconciliationAnomalies]);

  useEffect(() => {
    logPhase5A1ReactiveTrace({
      role,
      reactiveCount: reactiveWorkflowProblems.length,
      problemTypes: reactiveWorkflowProblems.map((p) => p.problemType),
      financeProblemsCount,
      financeOverdueKpi,
    });
  }, [role, reactiveWorkflowProblems, financeProblemsCount, financeOverdueKpi]);

  useEffect(() => {
    const monitor = process.env.REACT_APP_PHASE_P2_COMPLETION_MONITORING === '1';
    if (process.env.NODE_ENV !== 'development' && !monitor) return;
    if (!isFinanceRole(role) || isManagementRole(role)) return;
    const incomingTaskCount = allTasks.length;
    const semanticFinanceCount = allTasks.filter((t) => isFinanceWorkspaceTask(t)).length;
    const visibleScopedCount = scopedWorkspaceTasks.length;
    const finalQueueEntityCount =
      visibleRoutes.filter((route) => allTasks.some((t) => t.routeId === route.id && isFinanceWorkspaceTask(t))).length +
      visibleApplications.filter((app) => allTasks.some((t) => t.applicationId === app.id && isFinanceWorkspaceTask(t))).length;
    const statusOverdueCount = allTasks.filter((t) => String(t.status || '').toUpperCase() === 'OVERDUE').length;
    const selectorOverdueCount = allTasks.filter((t) => workspaceTaskIsOverdue(t)).length;
    console.debug('[PHASE_P2][dashboard-monitoring]', {
      role,
      queueFilter,
      incomingTaskCount,
      semanticFinanceCount,
      visibilityFilteredCount: visibleScopedCount,
      finalRenderedQueueEntityCount: finalQueueEntityCount,
      financeOverdueKpi,
      financeProblemsCount,
      visibleRoutesCount: visibleRoutes.length,
      visibleApplicationsCount: visibleApplications.length,
      statusOverdueCount,
      selectorOverdueCount,
      overdueSemanticsConsistent: statusOverdueCount === selectorOverdueCount,
      projectionClosureGaps: reactiveWorkflowProblems.filter((p) => p.problemType === 'closure_blocker').length,
    });
  }, [
    role,
    queueFilter,
    allTasks,
    scopedWorkspaceTasks,
    visibleRoutes,
    visibleApplications,
    financeOverdueKpi,
    financeProblemsCount,
    reactiveWorkflowProblems,
  ]);

  const applicationContextById = useMemo(() => {
    const m: Record<number, { basisDate?: string | null }> = {};
    for (const a of workspace?.myApplications ?? []) {
      m[a.id] = { basisDate: a.basisDate ?? null };
    }
    return m;
  }, [workspace]);

  const actionCenterItems = useMemo(() => {
    type Item = {
      key: string;
      color: string;
      title: string;
      hint?: string;
      actionLabel: string;
      task: WorkspaceTask | null;
      workspaceLink?: boolean;
      context?: { kind: 'route' | 'application'; id: number };
    };
    const severityColor = (s: 'warning' | 'attention' | 'critical') =>
      s === 'critical' ? 'red' : s === 'attention' ? 'orange' : 'gold';

    const items: Item[] = [];

    reactiveWorkflowProblems.forEach((p) => {
      items.push({
        key: p.key,
        color: severityColor(p.severity),
        title: p.title,
        hint: p.hint,
        actionLabel: p.workspaceLink ? 'Открыть в рабочем пространстве' : p.task ? 'Выполнить' : 'Открыть',
        task: p.task,
        workspaceLink: Boolean(p.workspaceLink && !p.task),
        context: p.context,
      });
    });

    if (isManagementRole(role) && governance?.prioritization?.rankedBlockers?.length) {
      governance.prioritization.rankedBlockers.slice(0, 2).forEach((b, i) => {
        items.push({
          key: `gov-${i}-${b.reasonCode}`,
          color: b.priorityLevel === 'CRITICAL' ? 'red' : 'orange',
          title: b.reason || b.criticalityExplanation || 'Требуется контроль',
          actionLabel: 'Перейти в рабочее пространство',
          task: null,
          workspaceLink: true,
        });
      });
    }

    const uniq = new Map<string, Item>();
    items.forEach((it) => {
      if (!uniq.has(it.key)) uniq.set(it.key, it);
    });
    return Array.from(uniq.values()).slice(0, 12);
  }, [reactiveWorkflowProblems, governance, role]);

  const refreshWorkspaceTasks = useCallback(async () => {
    try {
      const fresh = await dispatch(
        workspaceApi.endpoints.getWorkspace.initiate(undefined, { forceRefetch: true })
      ).unwrap();
      dispatch(
        workspaceApi.util.updateQueryData('getWorkspace', undefined, (draft) => {
          draft.overdueTasks = fresh.overdueTasks;
          draft.myTasks = fresh.myTasks;
          draft.myRoutes = fresh.myRoutes;
          draft.myApplications = fresh.myApplications;
          if (fresh.taskCount !== undefined) draft.taskCount = fresh.taskCount;
          if (fresh.overdueCount !== undefined) draft.overdueCount = fresh.overdueCount;
          if (fresh.routeCount !== undefined) draft.routeCount = fresh.routeCount;
          if (fresh.applicationCount !== undefined) draft.applicationCount = fresh.applicationCount;
        })
      );
    } catch {
      await refetch();
    }
  }, [dispatch, refetch]);

  const loading =
    isProfileLoading ||
    !shouldLoadDashboard ||
    isLoadingWorkspace ||
    (isManagementRole(role) && isLoadingStats) ||
    (needGovernanceForLayout && isLoadingGovernance) ||
    (shouldLoadFinanceQueues && (isLoadingIncomingObl || isLoadingOutgoingObl || isLoadingPaymentsWide));

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  const queueFilterSelect = (
    <Select<DashboardQueueFilter>
      value={queueFilter}
      onChange={setQueueFilter}
      style={{ width: 200 }}
      size="small"
      options={[
        { value: 'attention', label: 'Требуют внимания' },
        { value: 'active', label: 'В работе' },
        { value: 'requires_closure', label: 'Требуют закрытия' },
        { value: 'completed', label: 'Завершённые' },
        { value: 'all', label: 'Все' },
      ]}
    />
  );

  const attentionKpiTitle =
    isFinanceRole(role) && !isManagementRole(role) ? 'Требуют внимания (фин.)' : 'Требуют внимания';

  const kpiBar = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 12,
        alignItems: 'stretch',
        padding: '10px 14px',
        background: 'var(--ant-color-bg-container, #fff)',
        borderRadius: 8,
        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
        marginBottom: 10,
        overflowX: 'auto',
      }}
    >
      <div style={{ flex: '1 0 110px', minWidth: 100 }}>
        {isManagementRole(role) ? (
          <Tooltip title={`Финансовые: ${financeOverdueKpi} · Операционные: ${operationalOverdueKpi}`}>
            <Statistic
              title={primaryOverdueTitle}
              value={primaryOverdueKpi}
              valueStyle={{ fontSize: 22, color: '#cf1322' }}
            />
          </Tooltip>
        ) : (
          <Statistic
            title={primaryOverdueTitle}
            value={primaryOverdueKpi}
            valueStyle={{ fontSize: 22, color: '#cf1322' }}
          />
        )}
      </div>
      <div style={{ flex: '1 0 110px', minWidth: 100 }}>
        <Statistic title={attentionKpiTitle} value={queueKpis.attention} valueStyle={{ fontSize: 22, color: '#d46b08' }} />
      </div>
      {!(isFinanceRole(role) && !isManagementRole(role)) ? (
        <div style={{ flex: '1 0 110px', minWidth: 100 }}>
          <Statistic title="Маршруты в работе" value={routesInWorkCount} valueStyle={{ fontSize: 22 }} />
        </div>
      ) : null}
      <div style={{ flex: '1 0 110px', minWidth: 100 }}>
        <Statistic title="Заявки в работе" value={applicationsInWorkCount} valueStyle={{ fontSize: 22 }} />
      </div>
      {(isFinanceRole(role) || isManagementRole(role)) && (
        <div style={{ flex: '1 0 110px', minWidth: 100 }}>
          <Statistic title="Финансовые проблемы" value={financeProblemsCount} valueStyle={{ fontSize: 22, color: '#722ed1' }} />
        </div>
      )}
    </div>
  );

  const actionCenter = (
    <Card size="small" title="Что требует действий" styles={{ body: { paddingTop: 8 } }}>
      {actionCenterItems.length === 0 ? (
        <Text type="secondary">Сейчас нет срочных действий по вашим очередям.</Text>
      ) : (
        <List
          size="small"
          dataSource={actionCenterItems}
          renderItem={(item) => (
            <List.Item style={{ padding: '6px 0', borderBlockEnd: 'none' }}>
              <Space align="start" size={10} style={{ width: '100%' }}>
                <Badge color={item.color} />
                <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ display: 'block' }}>
                    {item.title}
                  </Text>
                  {item.hint ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.hint}
                    </Text>
                  ) : null}
                  <Space wrap size={8}>
                    {item.task ? (
                      <>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {taskLabel(item.task)}
                        </Text>
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setActiveTask(item.task)}>
                          {item.actionLabel}
                        </Button>
                      </>
                    ) : item.workspaceLink ? (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/workspace')}>
                        {item.actionLabel}
                      </Button>
                    ) : null}
                    {financeContextDrawerEnabled && item.context ? (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => {
                          const ctx = item.context;
                          if (!ctx) return;
                          openFinanceDrawerCtx(ctx);
                        }}
                      >
                        Контекст
                      </Button>
                    ) : null}
                  </Space>
                </Space>
              </Space>
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  const filterCard = (
    <Card size="small" title="Фильтр очереди" extra={queueFilterSelect} styles={{ body: { paddingTop: 6 } }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Те же правила, что в рабочем пространстве: только отбор строк, логика задач не меняется.
      </Text>
    </Card>
  );

  const financeMoneyPanel =
    shouldLoadFinanceQueues && (isFinanceRole(role) || isManagementRole(role)) ? (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <DashboardFinanceKpiStrip
          payments={paymentsWide}
          outgoingObligations={outgoingObligations}
          pendingExpenseConfirmations={pendingExpenseConfirmations}
          loading={isLoadingIncomingObl || isLoadingOutgoingObl || isLoadingPaymentsWide}
        />
        {isManagementRole(role) && stats ? (
          <Card size="small" title="Сводка по портфелю" styles={{ body: { paddingTop: 8 } }}>
            <Row gutter={[10, 6]}>
              <Col xs={12} sm={6}>
                <Statistic title="Доход (план)" value={stats.financialSummary?.totalClientRate ?? 0} precision={0} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Расходы (план)" value={stats.financialSummary?.totalRoutesCost ?? 0} precision={0} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Маржа (план)" value={stats.financialSummary?.totalProfit ?? 0} precision={0} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Заявок с оплатой" value={stats.receivedPaymentsApplications ?? 0} />
              </Col>
            </Row>
          </Card>
        ) : null}
        <Row gutter={[10, 10]}>
          <Col xs={24} lg={12}>
            <DashboardReceivablesBlock
              obligations={incomingObligations}
              loading={isLoadingIncomingObl}
              navigate={navigate}
              roleKind={dashboardRole}
              onOpenFinanceContext={financeContextDrawerEnabled ? openFinanceDrawerCtx : undefined}
            />
          </Col>
          <Col xs={24} lg={12}>
            <DashboardPayablesBlock
              obligations={outgoingObligations}
              loading={isLoadingOutgoingObl}
              navigate={navigate}
              roleKind={dashboardRole}
              onOpenFinanceContext={financeContextDrawerEnabled ? openFinanceDrawerCtx : undefined}
            />
          </Col>
        </Row>
      </Space>
    ) : null;

  const financeProblemsPanel =
    shouldLoadFinanceQueues && (isFinanceRole(role) || isManagementRole(role)) ? (
      <DashboardFinanceProblemsCard items={financeProblemItems} onOpenTask={(t) => setActiveTask(t)} />
    ) : null;

  const managementAnalyticsCollapse =
    isManagementRole(role) && governance ? (
      <Collapse
        bordered={false}
        style={{ marginTop: 10, background: 'transparent' }}
        defaultActiveKey={[]}
        items={[
          {
            key: 'sys',
            label: 'Системная аналитика',
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Card size="small" title="Технические показатели" styles={{ body: { paddingTop: 8 } }}>
                  <Row gutter={[10, 6]}>
                    <Col xs={12} sm={8}>
                      <Statistic
                        title="Критичные нарушения сроков"
                        value={governance?.slaSummary?.criticalBreach ?? 0}
                      />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="Просрочки задач" value={governance?.workflowMetrics?.overdueTasks ?? 0} />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="Зависшие процессы" value={governance?.workflowMetrics?.staleWorkflows ?? 0} />
                    </Col>
                  </Row>
                </Card>
                <GovernanceRiskHeader compact title="Детализация рисков и очередей" />
              </Space>
            ),
          },
        ]}
      />
    ) : null;

  const openTaskModal = (t: WorkspaceTask) => setActiveTask(t);

  const queuesOperations = (
    <DashboardCompactQueues
      mode="operations"
      routes={visibleRoutes}
      applications={visibleApplications}
      applicationContextById={applicationContextById}
      routeTaskCounts={routeTaskCounts}
      applicationTaskCounts={applicationTaskCounts}
      allTasks={allTasks}
      onInlineTaskAction={openTaskModal}
      buildRouteHref={(id) => routeCardPath(id, dashboardRole)}
      buildApplicationHref={(id) => applicationCardPath(id, dashboardRole)}
    />
  );

  const queuesFinance = (
    <DashboardCompactQueues
      mode="finance"
      routes={visibleRoutes}
      applications={visibleApplications}
      applicationContextById={applicationContextById}
      routeTaskCounts={routeTaskCounts}
      applicationTaskCounts={applicationTaskCounts}
      allTasks={allTasks}
      onInlineTaskAction={openTaskModal}
      maxRows={8}
      buildRouteHref={(id) => routeCardPath(id, dashboardRole)}
      buildApplicationHref={(id) => applicationCardPath(id, dashboardRole)}
      onOpenFinanceContext={financeContextDrawerEnabled ? openFinanceDrawerCtx : undefined}
    />
  );

  let mainQueues: React.ReactNode = null;
  if (isLogistLike(role)) {
    mainQueues = (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {queuesOperations}
        <DashboardLogistFinanceBlockers items={logistFinanceBlockers} />
      </Space>
    );
  } else if (isFinanceRole(role) && !isManagementRole(role)) {
    mainQueues = (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {financeMoneyPanel}
        {financeProblemsPanel}
        {queuesFinance}
      </Space>
    );
  } else if (isManagementRole(role)) {
    mainQueues = (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Row gutter={[10, 10]}>
          <Col xs={24} xl={14}>{queuesOperations}</Col>
          <Col xs={24} xl={10}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {queuesFinance}
            </Space>
          </Col>
        </Row>
        {financeMoneyPanel}
        {financeProblemsPanel}
        {managementAnalyticsCollapse}
      </Space>
    );
  } else {
    mainQueues = queuesOperations;
  }

  return (
    <PageLayout title="Центр действий" subtitle="Маршруты, заявки и задачи по приоритету">
      {kpiBar}

      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        <Col xs={24} lg={15}>
          {actionCenter}
        </Col>
        <Col xs={24} lg={9}>
          {filterCard}
        </Col>
      </Row>

      {mainQueues}

      <ApplicationTaskModalHost
        task={activeTask}
        onClose={() => setActiveTask(null)}
        onSuccess={refreshWorkspaceTasks}
      />
      <DashboardFinanceContextDrawer
        open={financeDrawerTarget != null}
        target={financeDrawerTarget}
        onClose={() => setFinanceDrawerTarget(null)}
        allTasks={allTasks}
        onInlineTaskAction={(t) => setActiveTask(t)}
        onSwitchTarget={(t) => setFinanceDrawerTarget(t)}
      />
    </PageLayout>
  );
};

export default DashboardPage;
